import type { ReactNode, RefObject } from 'react';
import type { ShellWidth } from '../../lib/shell/shell';
import { SiteFooter } from './SiteFooter';
import { SiteHeader } from './SiteHeader';
import { MAIN_CONTENT_ID, SkipToContent } from './SkipToContent';

/**
 * The Application Shell — every route that is not part of the public website.
 *
 * The layout classes are the ones the application already used, moved here
 * unchanged: `max-w-3xl` content width and the `px-4 md:px-8` responsive padding
 * pair from sections D.1 and D.5 of the visual system, and the existing `py-10`.
 * Nothing about how application routes render is altered by this slice beyond
 * the two additions the shell contract requires: the skip link, and a footer
 * that now carries the safety disclaimer the public site carries.
 *
 * The main-landmark ref is supplied by the caller rather than created here, for
 * the reason recorded on `ExperienceShell`: it has to outlive a shell change.
 */
export function ApplicationShell({
  children,
  mainRef,
  width = 'default',
}: {
  children: ReactNode;
  mainRef: RefObject<HTMLElement | null>;
  /**
   * `wide` is the admin console's measure, approved for it specifically. Every
   * other application route keeps the original `max-w-3xl` unchanged, so this
   * addition cannot alter a page that did not ask for it.
   */
  width?: ShellWidth;
}) {
  return (
    <div
      className={`mx-auto flex min-h-screen ${
        width === 'wide' ? 'max-w-6xl' : 'max-w-3xl'
      } flex-col gap-6 px-4 md:px-8 py-10`}
      data-shell-width={width}
    >
      <SkipToContent />
      <SiteHeader variant="app" />
      <main id={MAIN_CONTENT_ID} ref={mainRef} tabIndex={-1} className="focus-visible:outline-none">
        {children}
      </main>
      <SiteFooter variant="app" />
    </div>
  );
}
